/**
 * useTicket Hook
 *
 * Fetches a single ticket by ID using URQL.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const TicketDocument = `
  query Ticket($id: ID!) {
    ticket(id: $id) {
      id
      ticketNumber
      issuedAt
      ticketType {
        name
      }
      event {
        title
        startsAt
        venue
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface TicketData {
  id: string;
  ticketNumber: string;
  issuedAt: string;
  ticketType: { name: string };
  event: { title: string; startsAt: string; venue?: string };
}

interface UseTicketOptions {
  /** Whether to pause the query */
  pause?: boolean;
}

interface UseTicketResult {
  ticket: TicketData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch a single ticket by ID.
 *
 * Uses URQL for fetching ticket data.
 */
export function useTicket(
  ticketId: string | undefined,
  options: UseTicketOptions = {}
): UseTicketResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ ticket: TicketData | null }>({
    query: TicketDocument,
    variables: { id: ticketId },
    pause: pause || !ticketId,
  });

  return {
    ticket: result.data?.ticket ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
